import { CompletionSource } from '@codemirror/autocomplete';
import { javascript } from '@codemirror/lang-javascript';
import { LanguageSupport, LRLanguage } from '@codemirror/language';
import { Prec } from '@codemirror/state';
import {
  Decoration,
  DecorationSet,
  EditorView,
  MatchDecorator,
  ViewPlugin,
  ViewUpdate,
} from '@codemirror/view';
import { parseMixed } from '@lezer/common';
import { parser as jsonTemplatedParser } from '@persona-id/codemirror-lang-json-templated';

export function jsonTemplated(jsCompletionSources: CompletionSource[] = []) {
  const javascriptLang = javascript();

  const jsCompletionExtensions = jsCompletionSources.map((source) => {
    return javascriptLang.language.data.of({
      autocomplete: source,
    });
  });

  const parser = jsonTemplatedParser.configure({
    wrap: parseMixed((node) => {
      return node.type.isTop
        ? {
            parser: javascriptLang.language.parser,
            overlay: (node) => node.type.name === 'Template',
          }
        : null;
    }),
  });

  const language = LRLanguage.define({
    name: 'jsonjs',
    parser: parser,
  });

  const scriptDecorator = new MatchDecorator({
    regexp: /{{([^}]+)}}/g,
    decoration: Decoration.mark({ class: 'cm-persona-script-embed' }),
  });

  const scriptHighlight = ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;
      constructor(view: EditorView) {
        this.decorations = scriptDecorator.createDeco(view);
      }
      update(update: ViewUpdate) {
        this.decorations = scriptDecorator.updateDeco(update, this.decorations);
      }
    },
    {
      decorations: (v) => v.decorations,
    },
  );

  const bracesDecorator = new MatchDecorator({
    regexp: /{{|}}/g,
    decoration: Decoration.mark({ class: 'cm-persona-script-embed-braces' }),
  });

  const bracesHighlight = ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;
      constructor(view: EditorView) {
        this.decorations = bracesDecorator.createDeco(view);
      }
      update(update: ViewUpdate) {
        this.decorations = bracesDecorator.updateDeco(update, this.decorations);
      }
    },
    {
      decorations: (v) => v.decorations,
    },
  );

  const languageSupport = () =>
    new LanguageSupport(
      language,
      [
        [javascriptLang.support, ...jsCompletionExtensions],
        Prec.lowest([bracesHighlight, scriptHighlight]),
      ].flat(),
    );

  return [languageSupport()];
}
